import { LabReportTemplate } from './types';
import { CURRENT_SCHEMA_VERSION, DocumentSection } from '../../core/ast/document.types';
import { createDefaultPageSettings, createDefaultMetadata } from '../../core/ast/factory';
import { labReportCoverTemplate } from '../cover/labReportCover';
import { getDefaultCoverValues } from '../cover/templateUtils';

export const standardLabReportTemplate: LabReportTemplate = {
  id: 'tpl-lab-standard',
  name: 'Standard Academic Laboratory Report',
  category: 'general',
  description: 'Standard university laboratory format: Cover, Title, Objectives, Theory (Summary), Circuit Diagram, Pre-Lab, Experimental Datasheet, Post-Lab, and Discussion.',
  thumbnailBadge: 'Standard',
  version: 3,
  sectionsBlueprint: [
    {
      title: 'Cover Page',
      type: 'cover',
      defaultContent: '',
    },
    {
      title: 'Experiment Title',
      type: 'title',
      defaultContent: 'Experiment: [Enter Experiment Title Here]',
    },
    {
      title: 'Objectives',
      type: 'objectives',
      defaultContent: '1. \n2. ',
    },
    {
      title: 'Theory (Summary)',
      type: 'theory',
      defaultContent: '',
    },
    {
      title: 'Circuit Diagram',
      type: 'custom',
      defaultContent: 'Hardware schematic wiring configuration and component layout on breadboard.',
    },
    {
      title: 'Pre-Lab',
      type: 'custom',
      defaultContent: '',
    },
    {
      title: 'Experimental Datasheet',
      type: 'data',
      defaultContent: '',
    },
    {
      title: 'Post-Lab',
      type: 'custom',
      defaultContent: '',
    },
    {
      title: 'Discussion',
      type: 'discussion',
      defaultContent: '',
    },
  ],
  compileToDocument: (customTitle?: string, pageSettingsOverrides?) => {
    const now = new Date().toISOString();
    const coverDoc = labReportCoverTemplate.compileToDocument(getDefaultCoverValues(), pageSettingsOverrides);
    const coverSection = coverDoc.sections[0] || {
      id: `sec-std-0-${Date.now().toString(36)}`,
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks: [],
    };

    const remainingSections: DocumentSection[] = standardLabReportTemplate.sectionsBlueprint
      .filter((s) => s.type !== 'cover')
      .map((sec, idx) => {
        if (sec.type === 'theory') {
          return {
            id: `sec-std-${idx + 1}-${Date.now().toString(36)}`,
            title: sec.title,
            type: sec.type,
            enabled: true,
            collapsed: false,
            blocks: [
              {
                id: `blk-std-t1-${Date.now().toString(36)}`,
                type: 'text',
                createdAt: now,
                data: { text: sec.defaultContent || 'Enter theoretical background and governing equations.' },
              },
              {
                id: `blk-std-f1-${Date.now().toString(36)}`,
                type: 'formula',
                createdAt: now,
                data: {
                  latex: 'I_D = I_S \\left( e^{\\frac{q V_D}{n k T}} - 1 \\right)',
                  caption: 'Equation: Governing relationship',
                  displayMode: true,
                },
              },
            ],
          };
        }

        if (sec.type === 'data') {
          return {
            id: `sec-std-${idx + 1}-${Date.now().toString(36)}`,
            title: sec.title,
            type: sec.type,
            enabled: true,
            collapsed: false,
            blocks: [
              {
                id: `blk-std-tbl-${Date.now().toString(36)}`,
                type: 'table',
                createdAt: now,
                data: {
                  rows: 3,
                  cols: 3,
                  hasHeaderRow: true,
                  hasHeaderCol: false,
                  borderStyle: 'academic',
                  caption: 'Table 1: Experimental Measurements',
                  cells: [
                    [
                      { id: 'c00', content: 'Trial #', isHeader: true, bold: true, alignment: 'center' },
                      { id: 'c01', content: 'Parameter 1', isHeader: true, bold: true, alignment: 'center' },
                      { id: 'c02', content: 'Parameter 2', isHeader: true, bold: true, alignment: 'center' },
                    ],
                    [
                      { id: 'c10', content: '1', alignment: 'center' },
                      { id: 'c11', content: '', alignment: 'center' },
                      { id: 'c12', content: '', alignment: 'center' },
                    ],
                    [
                      { id: 'c20', content: '2', alignment: 'center' },
                      { id: 'c21', content: '', alignment: 'center' },
                      { id: 'c22', content: '', alignment: 'center' },
                    ],
                  ],
                },
              },
            ],
          };
        }

        return {
          id: `sec-std-${idx + 1}-${Date.now().toString(36)}`,
          title: sec.title,
          type: sec.type,
          enabled: true,
          collapsed: false,
          settings: { pageBreakBefore: sec.pageBreakBefore || false },
          blocks: [
            {
              id: `blk-std-${idx + 1}-${Date.now().toString(36)}`,
              type: 'text',
              createdAt: now,
              data: { text: sec.defaultContent },
            },
          ],
        };
      });

    const sections: DocumentSection[] = [coverSection, ...remainingSections];

    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      metadata: createDefaultMetadata(customTitle || 'Standard Laboratory Report', 'lab_report'),
      pageSettings: { ...createDefaultPageSettings(), ...pageSettingsOverrides },
      sections,
    };
  },
};
